/* Ludey newsletter popup — Maker 11. No external scripts, tracking or API keys. */
(() => {
  'use strict';
  if (customElements.get('ludey-popup')) return;

  const DAY = 86400000;
  const KEY = 'ludey:newsletter:v1';
  const PENDING = 'ludey:newsletter:pending';
  const fresh = () => ({ version: 1, count: 0, capAt: 0, declinedAt: 0, closedAt: 0,
    submitted: false, emailAt: 0, visitAt: 0, visitShown: false, lastShownAt: 0 });

  class LudeyPopup extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      try { this.config = JSON.parse(this.dataset.config); } catch (_) { return; }
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.form || typeof this.dialog.showModal !== 'function') return;
      this.editor = Boolean(window.Shopify?.designMode || this.config.designMode);
      this.elapsed = 0;
      this.lastTick = performance.now();
      this.ready = false;
      this.hasShown = false;
      this.returnResult = null;
      this.bindEvents();
      if (this.editor) {
        if (this.config.preview) this.open(false);
        return;
      }
      this.preparePrivacy().then(() => {
        if (!this.isConnected || this.signal.aborted) return;
        this.ready = true;
        this.consumeResult();
        this.observeEmailArrival();
        this.tick();
        this.timer = setInterval(() => this.tick(), 250);
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearInterval(this.timer);
      this.closeVisual();
      this.started = false;
    }

    on(target, event, listener, options = {}) {
      target.addEventListener(event, listener, { ...options, signal: this.signal });
    }

    bindEvents() {
      this.querySelectorAll('[data-ludey-close]').forEach(button => {
        this.on(button, 'click', () => this.dismiss(button.dataset.ludeyClose));
      });
      this.on(this.dialog, 'cancel', event => { event.preventDefault(); this.dismiss('close'); });
      this.on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right ||
            event.clientY < box.top || event.clientY > box.bottom) this.dismiss('close');
      });
      this.on(this.form, 'submit', event => {
        if (this.editor) {
          event.preventDefault();
          this.showResult('success');
          return;
        }
        // Keep Shopify's native submission and spam/challenge handling intact.
        // A submit click is NOT evidence of a successful subscription.
        try { sessionStorage.setItem(PENDING, String(Date.now())); } catch (_) { /* no memory */ }
      });
      this.on(window, 'pageshow', event => {
        if (event.persisted) { this.closeVisual(); this.hasShown = true; }
      });
      this.on(window, 'storage', event => {
        if (event.key === KEY && this.dialog.open) {
          const state = this.read();
          if ((this.config.hideSuccess && state.submitted) ||
              state.declinedAt || state.closedAt || (this.config.hideEmail && state.emailAt)) {
            this.closeVisual();
          }
        }
      });
      this.on(document, 'visitorConsentCollected', () => {
        if (!this.canRemember()) { this.closeVisual(); this.removeMemory(); }
        this.elapsed = 0;
        this.lastTick = performance.now();
        this.observeEmailArrival();
      });
      ['pointerdown', 'keydown', 'scroll'].forEach(name => {
        this.on(document, name, () => this.touchVisit(), { passive: true });
      });
      this.on(document, 'shopify:section:select', event => {
        if (this.editor && event.detail.sectionId === this.dataset.sectionId) { this.showResult('none'); this.open(false); }
      });
      this.on(document, 'shopify:section:deselect', event => {
        if (this.editor && event.detail.sectionId === this.dataset.sectionId) this.closeVisual();
      });
      this.on(document, 'shopify:block:select', event => {
        if (this.editor && this.contains(event.target)) { this.showResult('none'); this.open(false); }
      });
      this.on(document, 'shopify:block:deselect', event => {
        if (this.editor && this.contains(event.target)) this.closeVisual();
      });
    }

    preparePrivacy() {
      if (window.Shopify?.customerPrivacy || !window.Shopify?.loadFeatures) return Promise.resolve();
      return new Promise(resolve => {
        // Fail closed for automatic display if the consent API cannot be loaded.
        const timeout = setTimeout(() => { this.privacyFailed = true; resolve(); }, 6000);
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
            clearTimeout(timeout);
            this.privacyFailed = Boolean(error);
            resolve();
          });
        } catch (_) { clearTimeout(timeout); this.privacyFailed = true; resolve(); }
      });
    }

    canRemember() {
      if (this.editor || this.privacyFailed) return false;
      try {
        const privacy = window.Shopify?.customerPrivacy;
        if (privacy?.preferencesProcessingAllowed && !privacy.preferencesProcessingAllowed()) return false;
        if (!this.storageChecked) {
          localStorage.setItem(KEY + ':probe', '1');
          localStorage.removeItem(KEY + ':probe');
          this.storageChecked = true;
        }
        return true;
      } catch (_) { return false; }
    }

    read() {
      const state = fresh();
      if (!this.canRemember()) return state;
      try {
        const saved = JSON.parse(localStorage.getItem(KEY) || '{}');
        if (saved.version !== 1) return state;
        Object.keys(state).forEach(key => {
          if (typeof state[key] === 'number' && typeof saved[key] === 'number' &&
              Number.isFinite(saved[key]) && saved[key] >= 0) state[key] = saved[key];
          else if (typeof state[key] === 'boolean') state[key] = saved[key] === true;
        });
      } catch (_) { /* Invalid or old browser memory starts a new cycle. */ }
      return state;
    }

    write(state) {
      if (!this.canRemember()) return false;
      try { localStorage.setItem(KEY, JSON.stringify(state)); return true; } catch (_) { return false; }
    }

    removeMemory() {
      try { localStorage.removeItem(KEY); } catch (_) { /* storage is unavailable */ }
    }

    normalize(state, now) {
      const c = this.config;
      if (now - state.visitAt >= c.visitMinutes * 60000) state.visitShown = false;
      if (state.declinedAt && now - state.declinedAt >= c.noDays * DAY) state.declinedAt = 0;
      if (state.closedAt && now - state.closedAt >= c.closeDays * DAY) state.closedAt = 0;
      if (state.emailAt && now - state.emailAt >= c.emailDays * DAY) state.emailAt = 0;
      if (c.maxImpressions > 0 && state.count >= c.maxImpressions) {
        if (!state.capAt) state.capAt = state.lastShownAt || now;
        if (now - state.capAt >= c.impressionDays * DAY) {
          state.capAt = 0;
          state.count = 0;
        }
      }
      return state;
    }

    touchVisit() {
      if (this.editor || !this.ready || !this.canRemember() || document.hidden) return;
      const now = Date.now();
      if (this.lastTouch && now - this.lastTouch < 15000) return;
      this.lastTouch = now;
      const state = this.normalize(this.read(), now);
      state.visitAt = now;
      this.write(state);
    }

    observeEmailArrival() {
      if (!this.config.hideEmail || !this.canRemember()) return;
      const params = new URLSearchParams(location.search);
      const marker = String(this.config.emailMarker || '').trim();
      // Only a non-personal link marker or utm_medium=email; no email addresses in URLs/storage.
      if ((marker && params.get(marker) === '1') || params.get('utm_medium')?.toLowerCase() === 'email') {
        const state = this.read();
        state.emailAt = Date.now();
        this.write(state);
      }
    }

    consumeResult() {
      const result = this.querySelector('[data-ludey-result]')?.dataset.ludeyResult;
      let pending = 0;
      try { pending = Number(sessionStorage.getItem(PENDING)); } catch (_) { /* no session */ }
      const fromPopup = pending > 0 && Date.now() - pending < 30 * 60000;
      if (result === 'success') {
        // Only Liquid's server result confirms submission; never a click or our own URL flag.
        const state = this.read();
        state.submitted = true;
        this.write(state);
        if (fromPopup) this.returnResult = 'success';
      } else if (result === 'error' && fromPopup) this.returnResult = 'error';
      if (result === 'success' || result === 'error' || !fromPopup) {
        try { sessionStorage.removeItem(PENDING); } catch (_) { /* no session */ }
      }
    }

    excluded() {
      const root = window.Shopify?.routes?.root || '/';
      let path = location.pathname;
      if (root !== '/' && path.startsWith(root)) path = '/' + path.slice(root.length);
      return String(this.config.excludePaths || '').split(/[\n,]/).some(entry => {
        const prefix = entry.trim().replace(/\/$/, '');
        return prefix && (path === prefix || path.startsWith(prefix + '/'));
      });
    }

    blockers() {
      const viewport = document.querySelector('.layout--viewport');
      if (viewport?.dataset.modalStatus === 'opened') return true;
      if (viewport?.dataset.drawerStatus && viewport.dataset.drawerStatus !== 'closed') return true;
      const age = document.querySelector('modal-element[data-blocking="age_verification"]');
      if (age && !customElements.get('modal-element')) return true;
      const consent = document.querySelector('consent-tracking');
      if (consent && !customElements.get('consent-tracking')) return true;
      const candidates = document.querySelectorAll(
        'modal-element[aria-hidden="false"], consent-tracking[aria-hidden="false"], ' +
        'popup-block[aria-hidden="false"], menu-element[aria-hidden="false"], dialog[open], [aria-modal="true"], #shopify-pc__banner'
      );
      for (const item of candidates) {
        if (this.contains(item)) continue;
        if (item.getAttribute('data-transition-active') === 'false') continue;
        const style = getComputedStyle(item);
        if (style.display !== 'none' && style.visibility !== 'hidden' && item.getClientRects().length) return true;
      }
      return false;
    }

    eligible(state) {
      const c = this.config;
      if (!c.enabled || !this.canRemember() || this.excluded()) return false;
      const mobile = matchMedia('(max-width: 749px)').matches;
      if ((mobile && !c.mobile) || (!mobile && !c.desktop)) return false;
      if (c.hideKnown && c.knownSubscriber) return false;
      if (c.hideSuccess && state.submitted) return false;
      if (c.hideEmail && state.emailAt) return false;
      if (state.declinedAt || state.closedAt) return false;
      if (c.maxImpressions > 0 && state.capAt) return false;
      return !(c.oncePerVisit && state.visitShown);
    }

    tick() {
      const tick = performance.now();
      const delta = Math.min(500, tick - this.lastTick);
      this.lastTick = tick;
      if (this.editor || !this.ready) return;
      if (this.dialog.open) {
        if (this.blockers()) this.closeVisual();
        return;
      }
      if (this.hasShown || document.hidden) return;
      if (this.blockers()) { this.elapsed = 0; return; }
      if (this.returnResult) {
        this.showResult(this.returnResult);
        this.open(false);
        this.returnResult = null;
        return;
      }
      const now = Date.now();
      const state = this.normalize(this.read(), now);
      if (!this.eligible(state)) return;
      const focus = document.activeElement;
      if (focus?.matches('input, textarea, select, [contenteditable="true"]') && !this.contains(focus)) return;
      this.elapsed += delta;
      if (this.elapsed < this.config.delaySeconds * 1000) return;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = maxScroll > 0 ? window.scrollY / maxScroll * 100 : 100;
      if (scrollPercent < this.config.scrollPercent) return;
      this.open(true);
    }

    open(record) {
      if (this.dialog.open) return;
      if (record && !this.eligible(this.normalize(this.read(), Date.now()))) return;
      this.previousFocus = document.activeElement;
      this.querySelectorAll('img[data-src]').forEach(img => {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
      });
      try { this.dialog.showModal(); } catch (_) { return; }
      this.hasShown = true;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.locked = true;
      if (record) {
        const now = Date.now();
        const state = this.normalize(this.read(), now);
        state.visitAt = now;
        state.visitShown = true;
        state.lastShownAt = now;
        state.count += 1;
        if (this.config.maxImpressions > 0 && state.count >= this.config.maxImpressions) state.capAt = now;
        if (!this.write(state)) { this.closeVisual(); return; }
      }
      // Focus the heading, not the email input, so mobile keyboards stay closed initially.
      this.querySelector(this.resultShown === 'success' ? '[data-ludey-success-title]' : '[data-ludey-title]')?.focus({ preventScroll: true });
    }

    showResult(result) {
      this.resultShown = result;
      const success = result === 'success';
      this.querySelector('[data-ludey-intro]').hidden = success;
      this.querySelector('[data-ludey-fields]').hidden = success;
      this.querySelector('[data-ludey-success]').hidden = !success;
      this.dialog.setAttribute('aria-labelledby', success ? this.dataset.successTitleId : this.dataset.titleId);
    }

    dismiss(kind) {
      if (!this.editor && this.resultShown !== 'success' && kind !== 'done' && this.canRemember()) {
        const state = this.read();
        const now = Date.now();
        state.count = 0;
        state.capAt = 0;
        state.declinedAt = kind === 'no' ? now : 0;
        state.closedAt = kind === 'no' ? 0 : now;
        state.visitAt = now;
        state.visitShown = true;
        this.write(state);
      }
      this.closeVisual();
    }

    closeVisual() {
      if (this.dialog?.open) this.dialog.close();
      if (this.locked) {
        document.documentElement.style.overflow = this.previousOverflow || '';
        this.locked = false;
        if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('ludey-popup', LudeyPopup);
})();
